import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

type SoundType = 'click' | 'success' | 'error' | 'hover' | 'notification';

interface SoundContextType {
  isMuted: boolean;
  volume: number;
  toggleMute: () => void;
  setVolume: (volume: number) => void;
  playSound: (type: SoundType) => void;
}

const SoundContext = createContext<SoundContextType | null>(null);

const STORAGE_KEY = 'mirza-sound-preferences';

interface SoundPreferences {
  isMuted: boolean;
  volume: number;
}

// Audio synthesis functions for premium "glassy" sounds
class SoundSynthesizer {
  private audioContext: AudioContext | null = null;

  private getContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  // Soft, glassy click sound
  playClick(volume: number) {
    const ctx = this.getContext();
    const now = ctx.currentTime;

    // High-frequency sine wave for glassy feel
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(2400, now);
    osc.frequency.exponentialRampToValueAtTime(1200, now + 0.03);

    filter.type = 'highpass';
    filter.frequency.setValueAtTime(800, now);

    gain.gain.setValueAtTime(volume * 0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.1);
  }

  // Pleasant rising chime for success
  playSuccess(volume: number) {
    const ctx = this.getContext();
    const now = ctx.currentTime;

    // Create a pleasant chord progression
    const frequencies = [523.25, 659.25, 783.99]; // C5, E5, G5

    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * 0.05);
      osc.frequency.exponentialRampToValueAtTime(freq * 1.02, now + i * 0.05 + 0.15);

      gain.gain.setValueAtTime(0, now + i * 0.05);
      gain.gain.linearRampToValueAtTime(volume * 0.12, now + i * 0.05 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.05 + 0.3);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + i * 0.05);
      osc.stop(now + i * 0.05 + 0.35);
    });
  }

  // Low-tone thud for error
  playError(volume: number) {
    const ctx = this.getContext();
    const now = ctx.currentTime;

    // Low frequency thud
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(180, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + 0.1);

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(400, now);

    gain.gain.setValueAtTime(volume * 0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.2);

    // Add a second lower tone for depth
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();

    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(100, now + 0.02);
    osc2.frequency.exponentialRampToValueAtTime(60, now + 0.12);

    gain2.gain.setValueAtTime(volume * 0.1, now + 0.02);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

    osc2.connect(filter);
    gain2.connect(ctx.destination);

    osc2.start(now + 0.02);
    osc2.stop(now + 0.22);
  }

  // Very subtle hover sound
  playHover(volume: number) {
    const ctx = this.getContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(3500, now);
    osc.frequency.exponentialRampToValueAtTime(2800, now + 0.03);

    // Very low volume for hover
    gain.gain.setValueAtTime(volume * 0.03, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.05);
  }

  // Notification sound - gentle bell
  playNotification(volume: number) {
    const ctx = this.getContext();
    const now = ctx.currentTime;

    const frequencies = [880, 1174.66]; // A5, D6

    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * 0.1);

      gain.gain.setValueAtTime(0, now + i * 0.1);
      gain.gain.linearRampToValueAtTime(volume * 0.1, now + i * 0.1 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.4);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + i * 0.1);
      osc.stop(now + i * 0.1 + 0.45);
    });
  }
}

export function SoundProvider({ children }: { children: React.ReactNode }) {
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolumeState] = useState(0.5); // 50% default volume
  const synthRef = useRef<SoundSynthesizer | null>(null);

  // Initialize synthesizer
  useEffect(() => {
    synthRef.current = new SoundSynthesizer();
  }, []);

  // Load preferences from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const prefs: SoundPreferences = JSON.parse(stored);
        setIsMuted(prefs.isMuted);
        setVolumeState(prefs.volume);
      }
    } catch (e) {
      console.error('Failed to load sound preferences:', e);
    }
  }, []);

  // Save preferences to localStorage
  const savePreferences = useCallback((muted: boolean, vol: number) => {
    try {
      const prefs: SoundPreferences = { isMuted: muted, volume: vol };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
      console.error('Failed to save sound preferences:', e);
    }
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newValue = !prev;
      savePreferences(newValue, volume);
      return newValue;
    });
  }, [volume, savePreferences]);

  const setVolume = useCallback((vol: number) => {
    const clampedVol = Math.max(0, Math.min(1, vol));
    setVolumeState(clampedVol);
    savePreferences(isMuted, clampedVol);
  }, [isMuted, savePreferences]);

  const playSound = useCallback((type: SoundType) => {
    if (isMuted || !synthRef.current) return;

    try {
      switch (type) {
        case 'click':
          synthRef.current.playClick(volume);
          break;
        case 'success':
          synthRef.current.playSuccess(volume);
          break;
        case 'error':
          synthRef.current.playError(volume);
          break;
        case 'hover':
          synthRef.current.playHover(volume);
          break;
        case 'notification':
          synthRef.current.playNotification(volume);
          break;
      }
    } catch (e) {
      // Audio context might not be ready yet, ignore errors
    }
  }, [isMuted, volume]);

  return (
    <SoundContext.Provider value={{ isMuted, volume, toggleMute, setVolume, playSound }}>
      {children}
    </SoundContext.Provider>
  );
}

export function useSound() {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSound must be used within a SoundProvider');
  }
  return context;
}

// Optional hook for safe usage outside provider
export function useSoundOptional() {
  return useContext(SoundContext);
}